import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { TextInput, Select, Badge, Spinner } from 'flowbite-react';
import Title from '../../components/Title';
import Button from '../../components/Button';
import PaginationFlowBite from '../../components/PaginationFlowBite';
import DefaultModal from '../../components/modals/DefaultModal';
import ToastNotification from '../../components/ToastNotification';
import {
  customerAPI,
  ACCOUNT_MANAGER_FILTER_NONE,
  ASSIGN_MATCHING_MAX,
  buildCustomerSearchFilters,
  Customer,
  CustomerSearchParams,
  CustomerSortBy,
  CustomerStatusFilter,
  LastLoginBandFilter,
} from '../../api/customer';
import {
  accountManagersAPI,
  ACCOUNT_MANAGER_NOT_ACTIVE,
  getAccountManagerRefusal,
} from '../../api/account-managers';
import { usePermissions } from '../../hooks/usePermissions';
import {
  customerInformationStrings as S,
  formatMessage,
} from './CustomerInformation.strings';
import AccountManagerSelect from './AccountManagerSelect';
import {
  AccountManagerRoster,
  EMPTY_ACCOUNT_MANAGER_ROSTER,
  accountManagerName,
  assignmentFailureMessage,
  toAccountManagerRoster,
  useOperatorForWrite,
} from './accountManagerAssignment';
import { formatCount } from '../../utils/formatCount';
import {
  LAST_LOGIN_DOT_COLOR,
  formatLastLogin,
  lastLoginBand,
  lastLoginTooltip,
} from './lastLogin';
import {
  CustomerListQuery,
  DEFAULT_CUSTOMER_LIST_QUERY,
  readCustomerListQuery,
  toggleSort,
  withChange,
  writeCustomerListQuery,
} from './customerListQuery';
import SortableHeader from './SortableHeader';

/** Columns of the list table (the empty row spans all of them). */
const COLUMN_COUNT = 8;

/** Delay between the last keystroke in the search box and the request. */
export const CUSTOMER_SEARCH_DEBOUNCE_MS = 500;

const STATUS_OPTIONS: Array<{ value: CustomerStatusFilter; label: string }> = [
  { value: 'active', label: S.filter.active },
  { value: 'inactive', label: S.filter.inactive },
  { value: 'pending', label: S.filter.pending },
];

/** The legend's bands, in legend order; the gray "no access" dot is a display rule, not a band. */
const LEGEND_BANDS: Array<{ band: LastLoginBandFilter; label: string }> = [
  { band: 'recent', label: S.legend.recent },
  { band: 'stale', label: S.legend.stale },
  { band: 'old', label: S.legend.old },
  { band: 'never', label: S.legend.never },
];

/** The list query as sent to DS (`page` is 0-based in the client, as before). */
const toSearchParams = (query: CustomerListQuery): CustomerSearchParams => ({
  searchQuery: query.q || undefined,
  status: query.status || undefined,
  lastLoginBand: query.lastLoginBand || undefined,
  accountManager: query.accountManager || undefined,
  sortBy: query.sortBy,
  sortDir: query.sortDir,
  page: query.page - 1,
  perPage: query.perPage,
});

const CustomerInformation: React.FC = () => {
  const navigate = useNavigate();
  // The list state (search, filters, sort, page) lives in the URL query, so
  // reload and back/forward keep it; `customerListQuery.ts` holds the rules.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => readCustomerListQuery(searchParams), [searchParams]);
  const [searchInput, setSearchInput] = useState(query.q);
  const [roster, setRoster] = useState<AccountManagerRoster>(EMPTY_ACCOUNT_MANAGER_ROSTER);
  // Assigning needs the roster: until it loads (or if it fails) every member
  // would look unassigned, so the selects and the bulk bar stay disabled.
  const [rosterReady, setRosterReady] = useState(false);
  const { canAssignAccountManagers } = usePermissions();
  const canAssign = canAssignAccountManagers();
  const operatorForWrite = useOperatorForWrite();
  // The row whose manager is being saved (one write at a time); its select
  // shows the chosen key until the refetched row arrives.
  const [savingRow, setSavingRow] = useState<{ customerId: string; key: string | null } | null>(
    null,
  );
  const [bulkKey, setBulkKey] = useState('');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const updateQuery = useCallback(
    (change: (current: CustomerListQuery) => CustomerListQuery) => {
      setSearchParams((prev) => writeCustomerListQuery(change(readCustomerListQuery(prev))));
    },
    [setSearchParams],
  );

  // Keyed by the full query: only the current query's response is rendered,
  // so a slower response to an earlier query can never replace the rows of a
  // newer one. `keepPreviousData` keeps the table (and the focused header)
  // on screen while the next page or sort loads.
  const searchRequest = useMemo(() => toSearchParams(query), [query]);
  const {
    data,
    error,
    isError,
    isLoading,
    isFetching,
    isPreviousData,
    refetch,
  } = useQuery(
    ['abo-customer-search', searchRequest],
    () => customerAPI.searchCustomers(searchRequest),
    { keepPreviousData: true, refetchOnWindowFocus: false, retry: false },
  );

  const customers: Customer[] = data?.data ?? [];
  const total = data?.total ?? 0;
  // No response yet means the total is unknown, not zero: a "0" next to the
  // spinner reads as "no customers" on a slow load. Once a response exists,
  // `keepPreviousData` keeps its total on screen while the next query loads.
  const totalLabel = S.total.label.replace('{count}', data ? formatCount(total) : S.total.pending);
  const totalPages = data?.pages || 1;
  const hasActiveFilter = Boolean(
    query.q || query.status || query.lastLoginBand || query.accountManager,
  );

  // Every manager, active and inactive: a member can still point at a
  // deactivated one, whose name must resolve as "<name> (inactive)".
  const fetchAccountManagers = useCallback(async () => {
    try {
      setRoster(toAccountManagerRoster(await accountManagersAPI.getAllAccountManagers()));
      setRosterReady(true);
    } catch (error) {
      console.error('Error fetching account managers:', error);
      setRosterReady(false);
    }
  }, []);

  useEffect(() => {
    fetchAccountManagers();
  }, [fetchAccountManagers]);

  // A search box change reaches the URL (and page 1) after the debounce.
  useEffect(() => {
    if (searchInput === query.q) return undefined;
    const timer = setTimeout(() => {
      updateQuery((current) => withChange(current, { q: searchInput }));
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // query.q is read only to skip a no-op; the timer restarts per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, updateQuery]);

  // The URL can change the search from outside the box (back/forward, Clear filters).
  useEffect(() => {
    setSearchInput(query.q);
  }, [query.q]);

  // Hooks stay above the try so they run in the same order on every render.
  try {

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value as CustomerStatusFilter | '';
    updateQuery((current) => withChange(current, { status }));
  };

  const handleLastLoginBandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lastLoginBand = e.target.value as LastLoginBandFilter | '';
    updateQuery((current) => withChange(current, { lastLoginBand }));
  };

  const handleAccountManagerFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateQuery((current) => withChange(current, { accountManager: e.target.value }));
  };

  const handleSort = (column: CustomerSortBy) => {
    updateQuery((current) => toggleSort(current, column));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    updateQuery((current) =>
      withChange(current, {
        q: DEFAULT_CUSTOMER_LIST_QUERY.q,
        status: DEFAULT_CUSTOMER_LIST_QUERY.status,
        lastLoginBand: DEFAULT_CUSTOMER_LIST_QUERY.lastLoginBand,
        accountManager: DEFAULT_CUSTOMER_LIST_QUERY.accountManager,
      }),
    );
  };

  const handlePageChange = (newPage: number) => {
    updateQuery((current) => withChange(current, { page: newPage }));
  };

  const handlePerPage = (value: { value: number; label: string }) => {
    updateQuery((current) => withChange(current, { perPage: value.value }));
  };

  const handleRowClick = (customerId: string) => {
    navigate(`/customer-information/${customerId}`);
  };

  const handleRowAccountManagerChange = async (customer: Customer, key: string | null) => {
    if (savingRow) return;
    setSavingRow({ customerId: customer._id, key });
    try {
      await customerAPI.setCustomerAccountManager(customer._id, key, await operatorForWrite());
      ToastNotification({ type: 'success', message: S.accountManager.updated });
    } catch (saveError) {
      ToastNotification({ type: 'failure', message: assignmentFailureMessage(saveError) });
      if (getAccountManagerRefusal(saveError)?.code === ACCOUNT_MANAGER_NOT_ACTIVE) {
        fetchAccountManagers();
      }
    } finally {
      await refetch();
      setSavingRow(null);
    }
  };

  const bulkManager = bulkKey ? roster.byKey[bulkKey] : undefined;
  const bulkManagerName = bulkManager ? accountManagerName(bulkKey, roster) : '';
  const bulkTooLarge = total > ASSIGN_MATCHING_MAX;
  // The count the operator confirms is the one on screen: no Assign while a
  // newer count is loading, or before any count arrived.
  const canBulkAssign =
    rosterReady && Boolean(data) && Boolean(bulkManager) && total > 0 && !bulkTooLarge && !isFetching;
  const showBulkBar = canAssign && hasActiveFilter;

  const handleBulkAssign = async () => {
    if (bulkSaving || !bulkManager) return;
    setBulkSaving(true);
    try {
      const result = await customerAPI.assignMatchingAccountManager(
        {
          filters: buildCustomerSearchFilters(searchRequest),
          key: bulkKey,
          expectedCount: total,
        },
        await operatorForWrite(),
      );
      ToastNotification({
        type: 'success',
        message: formatMessage(S.accountManager.bulk.done, {
          count: formatCount(result.matched),
          name: bulkManagerName,
        }),
      });
    } catch (assignError) {
      // 409 count changed: the refetch below shows the list as it is now.
      ToastNotification({ type: 'failure', message: assignmentFailureMessage(assignError) });
      if (getAccountManagerRefusal(assignError)?.code === ACCOUNT_MANAGER_NOT_ACTIVE) {
        setBulkKey('');
        fetchAccountManagers();
      }
    } finally {
      setBulkSaving(false);
      setBulkConfirmOpen(false);
      refetch();
    }
  };

  /** A filter value that is no active manager (a bookmark of an inactive one) stays selectable. */
  const filterKeyIsExtra =
    Boolean(query.accountManager) &&
    query.accountManager !== ACCOUNT_MANAGER_FILTER_NONE &&
    !roster.active.some((manager) => manager.key === query.accountManager);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'failure';
      case 'pending':
        return 'warning';
      default:
        return 'gray';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const renderLastLogin = (customer: Customer) => {
    const band = lastLoginBand(customer.lastLogin, customer.status);
    return (
      <div className="flex items-center gap-2" data-testid="last-login-cell" data-band={band}>
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: LAST_LOGIN_DOT_COLOR[band] }}
        />
        <span className="text-gray-800" title={lastLoginTooltip(customer.lastLogin)}>
          {formatLastLogin(customer.lastLogin)}
        </span>
      </div>
    );
  };

  const tablePagination = {
    pageSize: query.perPage,
    total: total,
    lastPage: totalPages,
    currentPage: query.page,
    onChange: handlePageChange,
  };

  // Add error boundary
  if (isError) {
    return (
      <div className="pt-3 min-h-screen pb-[100px]">
        <Title>Customer Information</Title>
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-medium">Error loading customer information</h3>
          <p className="text-red-600 mt-1">
            {error instanceof Error ? error.message : 'Failed to fetch customers'}
          </p>
          <Button
            variant="LIGHT"
            onClick={() => {
              refetch();
            }}
            className="mt-3"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-3 min-h-screen pb-[100px]">
      <Title>Customer Information</Title>

      <div className="shadow-sm bg-white rounded-lg mt-6">
        <div className="flex justify-between border-b border-neutral-200 py-[22px] px-6 gap-6">
          <div className="flex gap-4 flex-1">
            <TextInput
              placeholder="Search by name or email"
              className="w-[400px]"
              value={searchInput}
              onChange={handleTextInput}
            />
            <Select
              aria-label={S.filter.accessLabel}
              value={query.status}
              onChange={handleStatusChange}
              className="w-[200px]"
            >
              <option value="">{S.filter.allAccess}</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label={S.filter.lastLoginLabel}
              value={query.lastLoginBand}
              onChange={handleLastLoginBandChange}
              className="w-[200px]"
            >
              <option value="">{S.filter.anyLastLogin}</option>
              {LEGEND_BANDS.map((item) => (
                <option key={item.band} value={item.band}>
                  {item.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label={S.accountManager.filterLabel}
              value={query.accountManager}
              onChange={handleAccountManagerFilterChange}
              className="w-[220px]"
            >
              <option value="">{S.accountManager.all}</option>
              <option value={ACCOUNT_MANAGER_FILTER_NONE}>{S.accountManager.notAssigned}</option>
              {filterKeyIsExtra && (
                <option value={query.accountManager}>
                  {accountManagerName(query.accountManager, roster)}
                </option>
              )}
              {roster.active.map((manager) => (
                <option key={manager.key} value={manager.key}>
                  {accountManagerName(manager.key, roster)}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            {isFetching && !isLoading && <Spinner size="sm" data-testid="customer-list-refreshing" />}
            <span data-testid="customer-list-total">{totalLabel}</span>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {showBulkBar && (
              <div
                className="flex flex-wrap items-center gap-3 border-b border-neutral-200 px-6 py-3 text-sm text-gray-700"
                data-testid="account-manager-bulk-bar"
              >
                <span>
                  {formatMessage(S.accountManager.bulk.label, {
                    count: data ? formatCount(total) : S.total.pending,
                  })}
                </span>
                <Select
                  sizing="sm"
                  aria-label={S.accountManager.bulk.managerLabel}
                  value={bulkManager ? bulkKey : ''}
                  onChange={(e) => setBulkKey(e.target.value)}
                  className="w-[220px]"
                >
                  <option value="">{S.accountManager.bulk.choose}</option>
                  {roster.active.map((manager) => (
                    <option key={manager.key} value={manager.key}>
                      {accountManagerName(manager.key, roster)}
                    </option>
                  ))}
                </Select>
                <Button
                  variant="LIGHT"
                  disabled={!canBulkAssign}
                  onClick={() => setBulkConfirmOpen(true)}
                >
                  {S.accountManager.bulk.assign}
                </Button>
                {bulkTooLarge && (
                  <span className="text-gray-500" data-testid="account-manager-bulk-too-large">
                    {formatMessage(S.accountManager.bulk.tooLarge, {
                      max: formatCount(ASSIGN_MATCHING_MAX),
                    })}
                  </span>
                )}
              </div>
            )}
            <div
              className="flex flex-wrap items-center gap-5 px-6 py-3 text-sm text-gray-700"
              data-testid="last-login-legend"
            >
              <span className="font-semibold text-gray-900">{S.legend.title}</span>
              {LEGEND_BANDS.map((item) => (
                <span key={item.band} className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: LAST_LOGIN_DOT_COLOR[item.band] }}
                  />
                  {item.label}
                </span>
              ))}
              <span className="text-gray-500">{S.legend.noAccessHint}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">Name</th>
                    <th scope="col" className="px-6 py-3">Email</th>
                    <th scope="col" className="px-6 py-3">Phone</th>
                    <th scope="col" className="px-6 py-3">Account Manager</th>
                    <th scope="col" className="px-6 py-3">{S.columns.access}</th>
                    <th scope="col" className="px-6 py-3 text-right" title={S.columns.creditsHint}>
                      {S.columns.credits}
                    </th>
                    <SortableHeader
                      column="lastLoginAt"
                      label={S.columns.lastLogin}
                      hint={S.columns.lastLoginHint}
                      sortBy={query.sortBy}
                      sortDir={query.sortDir}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      column="createdAt"
                      label={S.columns.created}
                      sortBy={query.sortBy}
                      sortDir={query.sortDir}
                      onSort={handleSort}
                    />
                  </tr>
                </thead>
                <tbody
                  aria-busy={isPreviousData && isFetching}
                  className={isPreviousData && isFetching ? 'opacity-60' : undefined}
                >
                  {customers.map((customer) => (
                    <tr
                      key={customer._id}
                      className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleRowClick(customer._id)}
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {customer.firstName} {customer.lastName}
                      </td>
                      <td className="px-6 py-4">{customer.email}</td>
                      <td className="px-6 py-4">{customer.phone || '-'}</td>
                      {canAssign ? (
                        // A click on the select must not open the member.
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <AccountManagerSelect
                            value={
                              savingRow?.customerId === customer._id
                                ? savingRow.key
                                : customer.accountManager
                            }
                            roster={roster}
                            ariaLabel={formatMessage(S.accountManager.selectLabel, {
                              name: `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || customer.email,
                            })}
                            disabled={!rosterReady || savingRow !== null}
                            onChange={(key) => handleRowAccountManagerChange(customer, key)}
                          />
                        </td>
                      ) : (
                        <td className="px-6 py-4">{accountManagerName(customer.accountManager, roster)}</td>
                      )}
                      <td className="px-6 py-4">
                        <Badge color={getStatusColor(customer.status)}>
                          {customer.status || 'Unknown'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-right tabular-nums" data-testid="credits-cell">
                        {typeof customer.credits === 'number' ? formatCount(customer.credits) : S.none}
                      </td>
                      <td className="px-6 py-4">{renderLastLogin(customer)}</td>
                      <td className="px-6 py-4">{formatDate(customer.createdAt)}</td>
                    </tr>
                  ))}
                  {customers.length === 0 && (
                    <tr>
                      <td colSpan={COLUMN_COUNT} className="px-6 py-4 text-center">
                        No customers found
                        {hasActiveFilter && (
                          <button
                            type="button"
                            onClick={handleClearFilters}
                            className="ml-3 font-medium text-primary-500 underline hover:no-underline"
                          >
                            {S.filter.clear}
                          </button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <PaginationFlowBite
                tablePagination={tablePagination}
                handlePerPage={handlePerPage}
              />
            )}
          </>
        )}
      </div>

      <DefaultModal
        title={formatMessage(S.accountManager.bulk.confirmTitle, {
          count: formatCount(total),
          name: bulkManagerName,
        })}
        showModal={bulkConfirmOpen}
        setShowModal={(show) => {
          if (!show && !bulkSaving) setBulkConfirmOpen(false);
        }}
        size="md"
      >
        {bulkConfirmOpen && (
          <div className="flex flex-col gap-4" data-testid="account-manager-bulk-confirm">
            <p className="text-sm text-neutral-600">{S.accountManager.bulk.confirmBody}</p>
            <div className="flex justify-end gap-2">
              <Button
                variant="LIGHT"
                disabled={bulkSaving}
                onClick={() => setBulkConfirmOpen(false)}
              >
                {S.accountManager.bulk.cancel}
              </Button>
              <Button isProcessing={bulkSaving} disabled={bulkSaving} onClick={handleBulkAssign}>
                {bulkSaving ? S.accountManager.bulk.assigning : S.accountManager.bulk.assign}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>
    </div>
  );
  } catch (renderError) {
    console.error('CustomerInformation render error:', renderError);
    return (
      <div className="pt-3 min-h-screen pb-[100px]">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-medium">Component Error</h3>
          <p className="text-red-600 mt-1">
            There was an error rendering the customer information page.
          </p>
          <p className="text-sm text-gray-600 mt-2">
            Check the browser console for details.
          </p>
        </div>
      </div>
    );
  }
};

export default CustomerInformation;